import React, { useState } from 'react';

import Modal from '~ComponentsImproved/Modal';
import NumberInput from '~ComponentsImproved/Numpad';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import { MethodProps } from '../../types';
import SearchStep from './Components/SearchStep';
import ConfirmStep from './Components/ConfirmStep';
import SendingStep from './Components/SendingStep';
import MessageStep from './Components/MessageStep';
import UnconfirmedStep from './Components/UnconfirmedStep';
import { useHotelRoomPayment } from '~Hooks/Payments/useHotelRoomPayment';
import { useI18n } from '~Hooks';

/** Charge the bill to a hotel room. All logic lives in useHotelRoomPayment; this only renders steps. */
const HotelRoomPayment: React.FunctionComponent<MethodProps> = (props) => {
  const { i18n } = useI18n();
  const hotel = useHotelRoomPayment(props);
  const { step } = hotel;

  const [showTipModal, setShowTipModal] = useState(hotel.askForTip);

  if (!hotel.ready || props.amount === undefined) {
    return null;
  }

  /* The tip comes first, as in the other methods, and goes with the charge and the order */
  if (showTipModal && hotel.wholeBill) {
    return (
      <NumberInput
        key="tip"
        initialValue={'0'}
        decimal
        title={i18n('TM.Tip')}
        onSubmit={async (value) => {
          const accepted = await hotel.enterTip(value);
          if (accepted) {
            setShowTipModal(false);
          }
        }}
        onClose={() => {
          setShowTipModal(false);
          hotel.cancel();
        }}
      />
    );
  }

  /** The heading follows the step; error steps let the message speak for itself. */
  const heading =
    step.kind === 'SEARCH'
      ? i18n('HotelRoom.FindGuest')
      : step.kind === 'CONFIRM'
      ? i18n('HotelRoom.Charge')
      : undefined;

  return (
    <Modal isShowing backdropClose={false} zIndex={49}>
      <ModalWrapper
        width="min(520px, 100%)"
        header={{ title: hotel.wholeBill && heading ? heading : undefined }}>
        {!hotel.wholeBill && (
          <MessageStep
            message={i18n('HotelRoom.WholeBillOnly')}
            onOtherPayment={() => hotel.otherPayment()}
          />
        )}

        {hotel.wholeBill && step.kind === 'SEARCH' && (
          <SearchStep
            tab={hotel.tab}
            search={hotel.search}
            searching={hotel.searching}
            loading={hotel.loading}
            failure={hotel.searchFailure}
            results={hotel.results}
            notice={step.notice}
            onTabChange={hotel.setTab}
            onSearchChange={hotel.setSearch}
            onSelect={hotel.selectTarget}
            onCancel={hotel.cancel}
          />
        )}

        {hotel.wholeBill && step.kind === 'CONFIRM' && (
          <ConfirmStep
            target={step.target}
            amount={props.amount}
            tip={hotel.tip}
            notice={step.notice}
            onConfirm={() => hotel.sendCharge(step.target)}
            onBack={hotel.backToSearch}
          />
        )}


        {hotel.wholeBill && step.kind === 'SENDING' && <SendingStep />}

        {hotel.wholeBill && step.kind === 'RETRY' && (
          <MessageStep
            message={step.message}
            primary={{
              label: i18n('HotelRoom.TryAgain'),
              onClick: () => hotel.sendCharge(step.target),
            }}
            // Without an answer, Cancel keeps the chargeId so the next attempt cannot charge twice
            onOtherPayment={step.canPayOtherwise ? hotel.otherPaymentAfterRetry : hotel.cancel}
            cancelOnly={!step.canPayOtherwise}
          />
        )}

        {hotel.wholeBill && step.kind === 'BLOCKED' && (
          <MessageStep
            message={step.message}
            primary={
              step.undoChargeId
                ? {
                    label: i18n('HotelRoom.Undo'),
                    onClick: () => hotel.undoCharge(step.undoChargeId!),
                  }
                : undefined
            }
            onOtherPayment={() => hotel.otherPayment()}
          />
        )}

        {hotel.wholeBill && step.kind === 'UNCONFIRMED' && (
          <UnconfirmedStep
            notice={step.notice}
            onAnswer={(onFolio) => hotel.resolveCharge(step.chargeId, onFolio, step.thenUndo)}
            onCancel={hotel.cancel}
          />
        )}
      </ModalWrapper>
    </Modal>
  );
};

export default HotelRoomPayment;
